<template>
    <data-iterator
        ref="iterator"
        :api="api"
        :filters.sync="filters"
        :fixed-filters="fixedFilters"
    >
        <template v-slot:actions>
            <slot name="actions"></slot>
        </template>

        <template slot-scope="{ items }">
            <d-table
                :items="items"
                :headers="headers"
                :filters.sync="filters"
                row-action
                @onRowSelected="onRowSelected"
            >
                <!-- name column -->
                <template v-slot:[`item.name`]="{ item }">
                    <router-link
                        v-if="isEditable(item)"
                        :to="getEditLink(item)"
                    >
                        {{ item.name }}
                    </router-link>
                    <span v-else>{{ item.name }}</span>
                </template>

                <!-- cutoffTime column -->
                <template v-slot:[`item.cutoffTime`]="{ item }">
                    {{ item.cutoffTime | transformDate }}
                </template>

                <!-- deliveryTime column -->
                <template v-slot:[`item.deliveryTime`]="{ item }">
                    {{ item.deliveryTime | transformDate }}
                </template>

                <!-- pickupOffset column -->
                <template v-slot:[`item.pickupOffset`]="{ item }">
                    {{
                        item.deliveryTime
                            | transformDate(-Number(item.pickupOffset))
                    }}
                </template>

                <!-- preCreateDeliveryEnabled column -->
                <template v-slot:[`item.preCreateDeliveryEnabled`]="{ item }">
                    <edit-button
                        v-if="item.preCreateDeliveryEnabled"
                        icon="more_time"
                        :to="{
                            name: routeType.DELIVERY_LIST,
                            query: {
                                'preCreatedFor[type]': 'residential_canteen',
                                'preCreatedFor[id]': item.id,
                            },
                        }"
                    ></edit-button>
                </template>

                <!-- isRecurring column -->
                <template v-slot:[`item.isRecurring`]="{ item }">
                    <v-chip outlined :color="item.isRecurring ? 'green' : ''">
                        {{ item.isRecurring ? 'Yes' : 'No' }}
                    </v-chip>
                </template>

                <!-- row actions -->
                <template v-slot:rowActions="{ item }">
                    <edit-button
                        v-if="isEditable(item)"
                        :to="getEditLink(item)"
                    ></edit-button>
                    <table-button
                        v-if="
                            item.preCreateDeliveryEnabled === false &&
                            $date(item.cutoffTime) > $date()
                        "
                        :tooltip="$t('actions.send_pre_delivery')"
                        color="blue-grey"
                        icon="two_wheeler"
                        approve-color="error"
                        @execute=";(dialog = true), (selectedItem = item)"
                    ></table-button>
                    <delete-action
                        v-if="isEditable(item)"
                        :entity-name="item.name"
                        :entity-id="item.id"
                        :api="api"
                        @deleted="onEntityDeleted"
                        @failed="onEntityDeleteFailed"
                    ></delete-action>
                </template>
            </d-table>
            <confirm-modal
                v-model="dialog"
                :title="$t('texts.request_courier')"
                :text="
                    $t('texts.do_you_want_request_courier', {
                        item: `${selectedItemIdentifier}`,
                    })
                "
                :order-id="selectedItemIdentifier"
                approve-color="green"
                :approve-text="$t('actions.confirm')"
                @onApprove="sendPreDelivery"
                @onCancel=";(selectedItem = null), (dialog = false)"
            ></confirm-modal>
        </template>
    </data-iterator>
</template>

<script>
import routeType from '@/router/routeType'
import RestApiType from '@/api/RestApiType'
import RestApiCollection from '@/api/RestApiCollection'
import DataIteratorMixin from '@/components/mixins/DataIteratorMixin'
import DataTableMixin from '@/components/mixins/DataTableMixin'
import Notification from '@/services/Notification/Notification'
import ConfirmModal from '@/components/confirm/ConfirmPreDeliveryModal'
import TableButton from '@/components/table/buttons/TableButton'
import Headers from '@/pages/residentialCanteens/mixins/Headers'

export default {
    components: {
        ConfirmModal,
        TableButton,
    },
    mixins: [DataIteratorMixin, DataTableMixin, Headers],
    props: {
        fixedFilters: {
            type: Object,
            default: () => {
                return {}
            },
        },
    },
    data() {
        return {
            api: RestApiType.RESIDENTIAL_CANTEENS,
            dialog: false,
            selectedItem: null,
            filters: {
                order: {
                    deliveryTime: 'desc',
                },
            },
        }
    },
    computed: {
        selectedItemIdentifier() {
            return this.selectedItem?.identifier || null
        },
    },
    methods: {
        getEditLink(item) {
            return {
                name: routeType.RESIDENTIAL_CANTEENS_EDIT,
                params: { entityId: item.id },
            }
        },
        isEditable(item) {
            return this.$date().isBefore(item.cutoffTime, 'second')
        },
        sendPreDelivery(deliveryMethod) {
            RestApiCollection.get(RestApiType.DELIVERIES)
                .preCreateDeliveryResidentialCC(
                    this.selectedItem.id,
                    deliveryMethod
                )
                .then((data) => {
                    if (data.success === false) {
                        Notification.error(
                            this.$t('notifications.pre_delivery_error.title', {
                                id: this.selectedItem.identifier,
                            }),
                            data.message
                        )
                    } else {
                        Notification.success(
                            this.$t(
                                'notifications.pre_delivery_success.title',
                                {
                                    id: this.selectedItem.identifier,
                                }
                            ),
                            data.message
                        )
                    }
                })
                .catch(() => {
                    this.$emit('failed', this.selectedItem.id)
                    Notification.error(
                        this.$t('notifications.pre_delivery_failed.title', {
                            id: this.selectedItem.identifier,
                        })
                    )
                })
                .finally(() => {
                    this.$refs.iterator.updateItems(true, true)
                })
        },
    },
}
</script>
